import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import OvernightPopupCard from "@/components/marketing/OvernightPopupCard";
import {
  useCopyOverride,
  usePromoActive,
  promoPreview,
  COPY_DEFAULTS,
} from "@/lib/copyOverrides";
import { sendPromoEvent } from "@/lib/promoEvents";


const STORAGE_KEY = "weekend-popup-shown";

const todayStamp = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const WeekendAvailabilityPopup = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const p = location.pathname;
  const onBookingRoute =
    p === "/book" ||
    p === "/book-now" ||
    p.startsWith("/book-now/") ||
    p.startsWith("/book5") ||
    p.startsWith("/pay/");
  const href = useCopyOverride("popup.href", COPY_DEFAULTS["popup.href"]);
  const delayRaw = useCopyOverride("popup.delayMs", COPY_DEFAULTS["popup.delayMs"]);
  const delayMs = (() => {
    const n = parseInt(delayRaw, 10);
    if (!Number.isFinite(n)) return 10000;
    return Math.min(120000, Math.max(1000, n));
  })();

  const popupActive = usePromoActive("popup");
  const preview = promoPreview() === "popup";

  useEffect(() => {
    if (!popupActive && !preview && open) setOpen(false);
  }, [popupActive, preview, open]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (onBookingRoute) return;
    if (preview) {
      const tp = window.setTimeout(() => setOpen(true), 500);
      return () => window.clearTimeout(tp);
    }
    if (!popupActive) return;
    const today = todayStamp();
    try {
      // Clear any legacy session flag so existing visitors aren't stuck.
      sessionStorage.removeItem(STORAGE_KEY);
      if (localStorage.getItem(STORAGE_KEY) === today) return;
    } catch {}
    const t = window.setTimeout(() => {
      setOpen(true);
      try {
        localStorage.setItem(STORAGE_KEY, today);
      } catch {}
    }, delayMs);
    return () => window.clearTimeout(t);
  }, [delayMs, onBookingRoute, popupActive, preview]);

  useEffect(() => {
    if (!open) return;
    sendPromoEvent("popup", "view");
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || onBookingRoute || (!popupActive && !preview)) return null;

  const close = () => setOpen(false);
  const onCta = () => {
    sendPromoEvent("popup", "click");
    close();
    if (/^https?:\/\//i.test(href)) {
      window.location.href = href;
    } else {
      navigate(href);
    }
  };


  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="weekend-popup-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={close}
    >
      <div
        className="relative w-full max-w-md bg-background rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-background/90 hover:bg-background text-foreground shadow-lg transition-colors"
        >
          <X className="w-7 h-7" strokeWidth={2.5} />
        </button>
        <h2 id="weekend-popup-title" className="sr-only">
          Overnight availability
        </h2>
        <OvernightPopupCard onCta={onCta} />
      </div>
    </div>
  );
};

export default WeekendAvailabilityPopup;